/**
 * T-226: plain words for the Alert summary card and the Profile time zone field.
 * The server decides whether alert emails are held for the summary; these only say so.
 */
import type { DigestSendRow, DigestSettings } from './alertDigestApi';

export type StatusTone = 'off' | 'on' | 'warn';

// Off says only that nothing is held: whether alert emails go out at all is the
// Alerts route's own switch (review on #1139).
const NOTHING_HELD = 'No alerts are held for a summary.';

/** Why the switch can't send, as the server reports it (resolveRouteSendable). */
const OFF_WHY: Record<string, string> = {
  kill_switch_env: 'switched off on the server',
  not_configured: 'email sending is not set up on this server',
  gate_error: 'its switch could not be read',
};

/** Why nothing is held while the switch is on (getDigestDeferral). */
const NOT_HOLDING_WHY: Record<string, string> = {
  schedule_off: 'the daily and weekly summaries are both off',
  no_recipients: 'no one is on the alert list',
  health_unknown: 'the summary\'s status could not be checked',
  gate_error: 'the summary\'s status could not be checked',
};

export function summaryStatus(s: Pick<DigestSettings, 'route' | 'holding'>): { tone: StatusTone; text: string } {
  if (!s.route.sendable) {
    if (s.route.reason === 'route_disabled') {
      return { tone: 'off', text: `Off. ${NOTHING_HELD} To start, turn on “Alert summary (daily and weekly)” under Email routes below.` };
    }
    const why = OFF_WHY[s.route.reason] ?? s.route.reason;
    return { tone: 'off', text: `Off: ${why}. ${NOTHING_HELD}` };
  }
  if (s.holding.active) {
    return { tone: 'on', text: 'On. Critical battery, below-15% and Sol-Ark outage alerts are emailed straight away; the other alerts go into the summary.' };
  }
  const reason = s.holding.reason;
  if (reason.startsWith('alerts_')) {
    return { tone: 'warn', text: 'On, but alert emails are switched off, so nothing is held for the summary.' };
  }
  if (reason === 'summary_failing') {
    return { tone: 'warn', text: 'On, but every alert is still emailed straight away until a summary has gone out to everyone on the alert list.' };
  }
  return { tone: 'warn', text: `On, but every alert is still emailed straight away: ${NOT_HOLDING_WHY[reason] ?? reason}.` };
}

export function sendRowWords(row: Pick<DigestSendRow, 'status' | 'reason'>): { label: string; tone: 'normal' | 'muted' | 'warn' } {
  if (row.status === 'sent') return { label: 'Sent', tone: 'normal' };
  if (row.status === 'running') return { label: 'Sending…', tone: 'muted' };
  if (row.status === 'failed') return { label: 'Failed', tone: 'warn' };
  const reason = row.reason ?? '';
  if (reason === 'empty') return { label: 'No alerts, nothing sent', tone: 'muted' };
  if (reason === 'suppressed') return { label: 'Not sent: address is bouncing', tone: 'warn' };
  if (reason.startsWith('rejected')) return { label: 'Not sent: address refused', tone: 'warn' };
  return { label: 'Skipped', tone: 'muted' };
}

/** The US zones the team works in, listed first. */
const COMMON_ZONES = [
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
];

/** "Pacific Time (Los Angeles)". */
export function timeZoneLabel(zone: string): string {
  const city = (zone.split('/').pop() ?? zone).replace(/_/g, ' ');
  let generic = '';
  try {
    generic = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longGeneric' })
      .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return zone;
  }
  return generic ? `${generic} (${city})` : city;
}

export function timeZoneOptions(): { common: string[]; all: string[] } {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  return { common: [...COMMON_ZONES], all: supported.filter((z) => !COMMON_ZONES.includes(z)) };
}
